/**
 * Intention de rattachement d'un sondage invité reçu par lien d'admin.
 *
 * Depuis le correctif du 28/09, la connexion ne rattache PLUS automatiquement
 * les sondages du trousseau (un co-organisateur connecté qui ouvrait le lien
 * d'admin transféré s'appropriait le sondage). Le rattachement devient
 * explicite : quand un invité clique « Créer un compte » dans la page d'admin,
 * on mémorise CE sondage ; après SIGNED_IN, `processPendingPollClaim` le
 * rattache — lui seul — puis efface l'intention.
 *
 * sessionStorage : survit à l'aller-retour OAuth dans le même onglet, meurt
 * avec l'onglet. Expire au bout de 30 min. Toute lecture/écriture est
 * protégée (stockage bloqué, navigation privée).
 */

const PENDING_POLL_CLAIM_KEY = "__dd_pending_poll_claim";
export const PENDING_POLL_CLAIM_TTL_MS = 30 * 60 * 1000;

/** Émis sur `window` après un rattachement explicite réussi. */
export const GUEST_POLL_CLAIMED_EVENT = "doodates:guest-poll-claimed";
/** Émis sur `window` après un échec de rattachement explicite. */
export const GUEST_POLL_CLAIM_FAILED_EVENT = "doodates:guest-poll-claim-failed";

export interface PendingPollClaim {
  pollId: string;
  slug?: string;
  createdAt: number;
}

export interface GuestPollClaimedDetail {
  pollId: string;
  count: number;
}

export interface GuestPollClaimFailedDetail {
  pollId: string;
  reason: "rate_limited" | "failed";
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function setPendingPollClaim(
  pollId: string,
  slug?: string,
  now: number = Date.now(),
): void {
  if (!pollId) return;
  try {
    storage()?.setItem(
      PENDING_POLL_CLAIM_KEY,
      JSON.stringify({ pollId, slug, createdAt: now }),
    );
  } catch {
    // Stockage indisponible : le bouton « Rattacher » restera proposé.
  }
}

export function clearPendingPollClaim(): void {
  try {
    storage()?.removeItem(PENDING_POLL_CLAIM_KEY);
  } catch {
    // ignore
  }
}

/** Intention valide et non expirée, sinon null (une intention périmée ou illisible est effacée). */
export function readPendingPollClaim(
  now: number = Date.now(),
): PendingPollClaim | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(PENDING_POLL_CLAIM_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingPollClaim>;
    if (
      typeof parsed?.pollId === "string" &&
      parsed.pollId &&
      typeof parsed.createdAt === "number" &&
      now - parsed.createdAt >= 0 &&
      now - parsed.createdAt <= PENDING_POLL_CLAIM_TTL_MS
    ) {
      return {
        pollId: parsed.pollId,
        slug: typeof parsed.slug === "string" ? parsed.slug : undefined,
        createdAt: parsed.createdAt,
      };
    }
  } catch {
    // JSON corrompu : effacé ci-dessous.
  }
  clearPendingPollClaim();
  return null;
}

function emit(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // Hors navigateur : rien à notifier.
  }
}

/**
 * Consomme l'intention après connexion : rattache exactement le sondage visé,
 * avec SON jeton du trousseau. Ignorée si expirée ou si le trousseau n'a pas
 * (ou plus) de jeton pour ce sondage. L'intention est effacée dans tous les
 * cas (un seul essai : en cas d'échec, le bouton « Rattacher » reste proposé
 * sur la page du sondage). Ne lève jamais.
 */
export async function processPendingPollClaim(
  fingerprint: string,
): Promise<number> {
  const intent = readPendingPollClaim();
  if (!intent) return 0;
  clearPendingPollClaim();

  try {
    const { getKeyringTokenForPoll } = await import("./guestIdentityService");
    const token = getKeyringTokenForPoll(intent.pollId, intent.slug);
    if (!token) return 0;

    const { pollService, GuestPollClaimError } =
      await import("../services/PollService");
    try {
      const count = await pollService.claimGuestPollWithToken(
        intent.pollId,
        token,
        fingerprint,
        intent.slug,
      );
      emit(GUEST_POLL_CLAIMED_EVENT, {
        pollId: intent.pollId,
        count,
      } satisfies GuestPollClaimedDetail);
      return count;
    } catch (error) {
      emit(GUEST_POLL_CLAIM_FAILED_EVENT, {
        pollId: intent.pollId,
        reason: error instanceof GuestPollClaimError ? error.reason : "failed",
      } satisfies GuestPollClaimFailedDetail);
      return 0;
    }
  } catch {
    return 0;
  }
}
